import React from 'react';
import { Globe, MapPin } from 'lucide-react';

interface Region {
  id: string;
  name: string;
  code: string;
  assetsCount: number;
  percentage: number;
  color: string;
}

interface AssetsDistributionMapProps {
  regions: Region[];
  totalAssets: number;
}

const AssetsDistributionMap: React.FC<AssetsDistributionMapProps> = ({
  regions,
  totalAssets,
}) => {
  return (
    <div className="bg-[#0F1629] rounded-xl p-6 relative overflow-hidden group">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Globe className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-white">Assets Distribution</h2>
          </div>
          <div className="text-sm text-gray-500">
            {totalAssets.toLocaleString()} total assets
          </div>
        </div>

        {/* Map Placeholder - In production, replace with actual map visualization */}
        <div className="aspect-video bg-gray-800/30 rounded-lg mb-6 relative overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            <Globe className="h-12 w-12 text-gray-700" />
          </div>
        </div>

        {/* Regions List */}
        <div className="grid grid-cols-2 gap-4">
          {regions.map((region) => (
            <div
              key={region.id}
              className="bg-gray-800/20 rounded-lg p-4 hover:bg-gray-800/30 transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <MapPin className={`h-4 w-4 ${region.color}`} />
                  <span className="text-sm font-medium text-gray-300">
                    {region.name}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-400">
                  {region.assetsCount}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{region.code}</span>
                  <span>{region.percentage}%</span>
                </div>
                <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${region.color} transition-all duration-500`}
                    style={{ width: `${region.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AssetsDistributionMap;